import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Grid,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import washService from "../../../services/wash.service";
import { IWashRequest } from "../../../types/wash.types";
import { logger } from "../../../utils/logger";
import { ReviewSummary } from "../../../components/WashHistoryTable/WashHistoryTable";

const STATUS_LABEL_KEYS: Record<string, string> = {
  pending: "washDetail.statusPending",
  accepted: "washDetail.statusAccepted",
  in_progress: "washDetail.statusInProgress",
  completed: "washDetail.statusCompleted",
  cancelled: "washDetail.statusCancelled",
  not_arrive: "washDetail.statusNotArrived",
};

const SERVICE_TYPE_LABEL_KEYS: Record<string, string> = {
  inner: "vehicle.serviceInner",
  outer: "vehicle.serviceOuter",
  both: "vehicle.serviceBoth",
};

const WashManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [washes, setWashes] = useState<IWashRequest[]>([]);
  const [filteredWashes, setFilteredWashes] = useState<IWashRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");

  const fetchWashes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await washService.getAllWashes();
      setWashes(data);
      logger.info("Fetched washes", data.length);
    } catch (err: any) {
      setError(err.message || t("washManagement.fetchFailed"));
      logger.error("Failed to fetch washes", err);
    } finally {
      setLoading(false);
    }
  }, [t]);

  const filterWashes = useCallback(() => {
    let filtered = [...washes];

    // Filter by status
    if (statusFilter !== "all") {
      filtered = filtered.filter((wash) => wash.status === statusFilter);
    }

    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (wash) =>
          wash.customer?.firstName?.toLowerCase().includes(search) ||
          wash.customer?.lastName?.toLowerCase().includes(search) ||
          wash.customer?.phone?.includes(search) ||
          wash.carWash?.legalName?.toLowerCase().includes(search) ||
          wash._id?.toLowerCase().includes(search)
      );
    }

    setFilteredWashes(filtered);
  }, [washes, statusFilter, searchTerm]);

  useEffect(() => {
    fetchWashes();
  }, [fetchWashes]);

  useEffect(() => {
    filterWashes();
  }, [filterWashes]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewDetails = (washId: string) => {
    navigate(`/dashboard/washes/${washId}`, { state: { from: location.pathname } });
  };

  const getStatusColor = (
    status: string
  ): "default" | "primary" | "secondary" | "error" | "warning" | "info" | "success" => {
    switch (status) {
      case "pending":
        return "warning";
      case "accepted":
        return "info";
      case "in_progress":
        return "primary";
      case "completed":
        return "success";
      case "cancelled":
      case "not_arrive":
        return "error";
      default:
        return "default";
    }
  };

  const getServiceTypeLabel = (type: string): string =>
    SERVICE_TYPE_LABEL_KEYS[type] ? t(SERVICE_TYPE_LABEL_KEYS[type]) : type;

  const getStatusLabel = (status: string): string =>
    STATUS_LABEL_KEYS[status]
      ? t(STATUS_LABEL_KEYS[status])
      : status.replace("_", " ").toUpperCase();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="p-4">
      <Paper className="p-6">
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5">{t("washManagement.title")}</Typography>
          <Tooltip title={t("common.refresh")}>
            <IconButton onClick={fetchWashes} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Filters */}
        <Grid container spacing={2} mb={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label={t("common.search")}
              placeholder={t("washManagement.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              select
              label={t("washManagement.status")}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              size="small"
            >
              <MenuItem value="all">{t("status.all")}</MenuItem>
              <MenuItem value="pending">{t("washDetail.statusPending")}</MenuItem>
              <MenuItem value="accepted">
                {t("washDetail.statusAccepted")}
              </MenuItem>
              <MenuItem value="in_progress">
                {t("washDetail.statusInProgress")}
              </MenuItem>
              <MenuItem value="completed">
                {t("washDetail.statusCompleted")}
              </MenuItem>
              <MenuItem value="cancelled">
                {t("washDetail.statusCancelled")}
              </MenuItem>
              <MenuItem value="not_arrive">
                {t("washDetail.statusNotArrived")}
              </MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Chip
                label={t("washManagement.totalCount", {
                  count: washes.length,
                })}
                color="default"
                size="small"
              />
              <Chip
                label={t("washManagement.pendingCount", {
                  count: washes.filter((w) => w.status === "pending").length,
                })}
                color="warning"
                size="small"
              />
              <Chip
                label={t("washManagement.completedCount", {
                  count: washes.filter((w) => w.status === "completed").length,
                })}
                color="success"
                size="small"
              />
            </Box>
          </Grid>
        </Grid>

        {/* Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("washManagement.colWashId")}</TableCell>
                <TableCell>{t("washingHistory.colCustomer")}</TableCell>
                <TableCell>{t("washingHistory.colCarWash")}</TableCell>
                <TableCell>{t("washManagement.colService")}</TableCell>
                <TableCell>{t("common.price")}</TableCell>
                <TableCell>{t("common.status")}</TableCell>
                <TableCell>{t("common.date")}</TableCell>
                <TableCell>{t("washingHistory.colCustomerReview")}</TableCell>
                <TableCell align="center">{t("common.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredWashes
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((wash) => (
                  <TableRow
                    key={wash._id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => handleViewDetails(wash._id)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {wash._id.slice(-8)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {[wash.customer?.firstName, wash.customer?.lastName]
                          .filter(Boolean)
                          .join(" ") || "—"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {wash.customer?.country_code} {wash.customer?.phone}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {wash.carWash?.legalName ??
                          t("washManagement.notAssigned")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {wash.carWash?.location?.street ||
                          (wash.carWash?.location as { streetName?: string } | undefined)
                            ?.streetName ||
                          "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {getServiceTypeLabel(wash.serviceType)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {wash.vehicleDetails.type || t("common.notAvailable")}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {wash.price} ₼
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("washManagement.commission", {
                          amount: wash.adminCommission,
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(wash.status)}
                        color={getStatusColor(wash.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(wash.createdAt).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(wash.createdAt).toLocaleTimeString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <ReviewSummary wash={wash} />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={t("washManagement.viewDetails")}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetails(wash._id);
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              {filteredWashes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body2" color="text.secondary" py={4}>
                      {t("washManagement.empty")}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredWashes.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
};

export default WashManagement;
